import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Paper,
  alpha,
  styled
} from '@mui/material';
import Grid from '../components/Grid';
import { Email, Phone, LocationOn, AccessTime, Send as SendIcon } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { useLocation } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  preferredDateTime: string;
}

const initialFormData: ContactFormData = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  preferredDateTime: ''
};

const StyledContactCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 30px rgba(0,0,0,0.08)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [isPremiumRequest, setIsPremiumRequest] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if there's prefilled data in the location state
    if (location.state?.prefill) {
      setFormData(prevData => ({
        ...prevData,
        ...location.state.prefill
      }));
      
      // Check if this is a premium request
      if (location.state.prefill.subject === 'Premium Toegang Aanvraag') {
        setIsPremiumRequest(true);
      }
    }
  }, [location]);

  const handleChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setSnackbar({
      open: true,
      message: language === 'nl' 
        ? 'Bedankt voor je bericht! Ik neem zo spoedig mogelijk contact met je op.' 
        : 'Thank you for your message! I will contact you as soon as possible.',
      severity: 'success'
    });
    setFormData(initialFormData);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <PageHeader
        title={translations[language].contact.title}
        subtitle={translations[language].contact.subtitle}
      />

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={6}>
          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <StyledContactCard>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ 
                  fontWeight: 500,
                  color: 'primary.main',
                  mb: 3
                }}>
                  {translations[language].contact.contactInfo.title}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Email sx={{ mr: 2, color: 'primary.main', fontSize: 24, mt: 0.5 }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {translations[language].contact.contactInfo.email}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        sterk-aanhetwerk@outlook.com
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <AccessTime sx={{ mr: 2, color: 'primary.main', fontSize: 24, mt: 0.5 }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {translations[language].contact.contactInfo.availability.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {translations[language].contact.contactInfo.availability.days}<br />
                        {translations[language].contact.contactInfo.availability.hours}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 2 }}>
                    <Box sx={{ mr: 2, width: 24, display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {translations[language].contact.contactInfo.company.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {translations[language].common.kvk}: 90066200<br />
                        {translations[language].common.btw}: NL004785209B28
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </StyledContactCard>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <StyledContactCard>
              <CardContent sx={{ p: 4 }}>
                {isPremiumRequest && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    {language === 'nl'
                      ? 'Je hebt interesse getoond in premium toegang. Vul het formulier verder in om je aanvraag af te ronden.'
                      : 'You have shown interest in premium access. Complete the form to finalize your request.'}
                  </Alert>
                )}
                
                <Typography variant="h5" gutterBottom sx={{ 
                  fontWeight: 500,
                  color: 'primary.main',
                  mb: 3
                }}>
                  {translations[language].contact.form.title}
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        required
                        fullWidth
                        label={translations[language].contact.form.name}
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        required
                        fullWidth
                        label={translations[language].contact.form.email}
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        label={translations[language].contact.form.phone}
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        required
                        fullWidth
                        label={translations[language].contact.form.subject}
                        value={formData.subject}
                        onChange={(e) => handleChange('subject', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        required
                        fullWidth
                        label={translations[language].contact.form.message}
                        multiline
                        minRows={4}
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        label={language === 'nl' ? 'Voorkeur voor datum en tijdstip van onze eerste afspraak' : 'Preferred date and time for our first appointment'}
                        value={formData.preferredDateTime}
                        onChange={(e) => handleChange('preferredDateTime', e.target.value)}
                        placeholder={language === 'nl' ? 'Bijvoorbeeld: 15 maart, 14:00 uur' : 'For example: March 15, 2:00 PM'}
                      />
                    </Grid>
                  </Grid>

                  <Button 
                    type="submit"
                    variant="contained" 
                    color="primary"
                    size="large"
                    endIcon={<SendIcon />}
                    sx={{ 
                      mt: 4,
                      px: 4,
                      py: 1.5,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: '1rem'
                    }}
                  >
                    {translations[language].contact.form.submit}
                  </Button>
                </form>
              </CardContent>
            </StyledContactCard>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Contact; 